// src/pages/ResultatsPage/ResultatsPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  FaFilePdf,
  FaCamera,
  FaSearch,
  FaFilter,
  FaUndo,
} from "react-icons/fa";
import { getAllEvents } from "../../services/eventServices";
import { ToastContainer, toast } from "react-toastify";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import useScrollToTop from "../../hooks/useScrollToTop";
import formatDate from "../../utils/formatDate";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import PageHeader from "../../components/Layout/PageHeader";

const cateEvents = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
];

// Calcule la saison sportive à partir d'une date (1er août -> 31 juillet)
const getSeasonFromDate = (dateInput) => {
  const d = new Date(dateInput);
  if (isNaN(d)) return null;

  const year = d.getFullYear();
  const month = d.getMonth(); // 0 = janvier, 7 = août
  let startYear, endYear;

  if (month >= 7) {
    // Août -> Décembre : saison year / year+1
    startYear = year;
    endYear = year + 1;
  } else {
    // Janvier -> Juillet : saison year-1 / year
    startYear = year - 1;
    endYear = year;
  }

  const start = new Date(startYear, 7, 1, 0, 0, 0, 0); // 1er août
  const end = new Date(endYear, 6, 31, 23, 59, 59, 999); // 31 juillet
  const key = `${startYear}-${endYear}`;
  const label = key;

  return { key, label, start, end };
};

// Saison sportive "courante" basée sur aujourd'hui
const getCurrentSeason = () => getSeasonFromDate(new Date());

const ResultatsPage = () => {
  useScrollToTop();

  const [allEvents, setAllEvents] = useState([]); // tous les événements (toutes saisons) filtrés sur niveau/type
  const [seasons, setSeasons] = useState([]); // [{key, label, start, end}, ...]
  const [selectedSeasonKey, setSelectedSeasonKey] = useState("");
  const [currentSeasonKey, setCurrentSeasonKey] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsData = await getAllEvents();

        // 1) Filtrer sur niveau + type
        const baseFiltered = (eventsData || []).filter(
          (event) =>
            event.level_event === "Départemental" &&
            (event.type_event === "Animation" ||
              event.type_event === "Compétition")
        );

        // Tri global par date décroissante
        baseFiltered.sort(
          (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
        );

        setAllEvents(baseFiltered);

        // 2) Construire la liste des saisons à partir des événements
        const seasonMap = {};
        baseFiltered.forEach((event) => {
          const season = getSeasonFromDate(event.start);
          if (!season) return;
          if (!seasonMap[season.key]) {
            seasonMap[season.key] = season;
          }
        });

        const seasonsArray = Object.values(seasonMap).sort(
          (a, b) => b.start.getTime() - a.start.getTime()
        );
        setSeasons(seasonsArray);

        // 3) Déterminer la saison courante + saison sélectionnée par défaut
        const currentSeason = getCurrentSeason();
        setCurrentSeasonKey(currentSeason?.key || "");

        if (seasonMap[currentSeason.key]) {
          // Si on a des compétitions pour la saison courante
          setSelectedSeasonKey(currentSeason.key);
        } else if (seasonsArray.length > 0) {
          // Sinon on prend la saison la plus récente disponible
          setSelectedSeasonKey(seasonsArray[0].key);
        } else {
          setSelectedSeasonKey("");
        }
      } catch (error) {
        console.error("Erreur lors du chargement des événements:", error);
        toast.error("Erreur lors du chargement des événements");
      }
    };

    fetchEvents();
  }, []);

  // Map [key] => season pour accès rapide
  const seasonMap = useMemo(() => {
    const map = {};
    seasons.forEach((s) => {
      map[s.key] = s;
    });
    return map;
  }, [seasons]);

  // Événements de la saison sélectionnée
  const seasonEvents = useMemo(() => {
    if (!selectedSeasonKey || !seasonMap[selectedSeasonKey]) return allEvents;

    const { start, end } = seasonMap[selectedSeasonKey];
    return allEvents.filter((event) => {
      const d = new Date(event.start);
      if (isNaN(d)) return false;
      return d >= start && d <= end;
    });
  }, [allEvents, selectedSeasonKey, seasonMap]);

  // Application des filtres (texte + catégorie) sur la saison sélectionnée
  const displayedEvents = useMemo(() => {
    let filtered = [...seasonEvents];

    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      filtered = filtered.filter((event) =>
        event.titre.toLowerCase().includes(t)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter((event) => {
        if (!event.cate_event) return false;
        const cats = event.cate_event
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean);
        return cats.some((cat) => cat.includes(selectedCategory));
      });
    }

    return filtered;
  }, [seasonEvents, searchTerm, selectedCategory]);

  const totalResults = displayedEvents.length;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleSeasonChange = (e) => {
    setSelectedSeasonKey(e.target.value);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
  };

  const currentSeasonLabel =
    (selectedSeasonKey && seasonMap[selectedSeasonKey]?.label) ||
    seasons[0]?.label ||
    "";

  return (
    <div className="min-h-screen flex flex-col w-full mx-auto pt-[130px] sm:pt-[130px] md:pt-[150px] lg:pt-[150px] xl:pt-[150px] pb-10 bg-slate-50 dark:bg-slate-900 dark:text-white">
      <ScrollIndicator />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <PageHeader
          eyebrow="Visiteurs & Utilisateurs"
          title="Listing des Résultats Sportifs"
          subtitle="Retrouvez ici les liens vers les rapports, tableaux, sélections et photos des compétitions et animations départementales. Vous pouvez changer de saison grâce au sélecteur dédié."
          align="center"
          size="lg"
        />

        <InfoBubble title="Bienvenue sur la page Résultats Sportifs">
          <p className="text-sm">
            Filtrez les résultats par saison sportive (du 1ᵉʳ août au 31
            juillet), par titre de compétition et par catégorie d&apos;âge. Les
            compétitions départementales (Animations &amp; Compétitions) sont
            listées avec leurs rapports, tableaux, sélections et photos.
          </p>
        </InfoBubble>
      </div>

      {/* Bloc filtres */}
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg">
          {/* Ligne couleur */}
          <div className="h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />

          <div className="p-4 md:p-5 space-y-4">
            {/* Ligne 1 : saison + reset */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              {/* Sélecteur de saison */}
              <div className="w-full md:max-w-xs">
                <label className="text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300 mb-1 block">
                  Saison sportive
                </label>
                <div className="relative">
                  <FaFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                  <select
                    className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800/90 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/70"
                    value={selectedSeasonKey}
                    onChange={handleSeasonChange}
                  >
                    {seasons.length === 0 && (
                      <option value="">Aucune saison disponible</option>
                    )}
                    {seasons.map((season) => (
                      <option key={season.key} value={season.key}>
                        Saison {season.label}
                        {season.key === currentSeasonKey ? " (en cours)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Badge + reset */}
              <div className="flex flex-col items-start md:items-end gap-2 w-full md:w-auto">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
                    Saison affichée
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-semibold">
                    {currentSeasonLabel || "—"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300 hover:text-primary dark:hover:text-primary"
                >
                  <FaUndo className="text-[10px]" />
                  Réinitialiser les filtres
                </button>
              </div>
            </div>

            {/* Ligne 2 : recherche + catégorie + compteur */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              {/* Recherche */}
              <div className="w-full md:max-w-md">
                <label className="text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300 mb-1 block">
                  Recherche par compétition / animation
                </label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                  <input
                    type="text"
                    placeholder="Ex : Coupe minimes, Animation poussin(e)s..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800/90 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/70"
                  />
                </div>
              </div>

              {/* Catégorie */}
              <div className="w-full md:max-w-xs">
                <label className="text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300 mb-1 block">
                  Catégorie d&apos;âge
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800/90 text-sm text-slate-900 dark:text-white py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/70"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                >
                  <option value="">Toutes les catégories</option>
                  {cateEvents.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Compteur */}
              <div className="text-xs text-slate-500 dark:text-slate-300 md:text-right">
                <span className="font-semibold text-primary">
                  {totalResults}
                </span>{" "}
                compétition(s) / animation(s) trouvée(s) pour la saison{" "}
                {currentSeasonLabel || "—"}.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table des résultats */}
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="overflow-x-auto rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white/95 dark:bg-slate-900/95 shadow-lg">
          <table className="min-w-full text-sm font-montserrat border-collapse">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-4 md:px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em]">
                  Date
                </th>
                <th className="px-4 md:px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em]">
                  Compétition / Animation
                </th>
                <th className="px-4 md:px-5 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                  Rapport
                </th>
                <th className="px-4 md:px-5 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                  Tableaux
                </th>
                <th className="px-4 md:px-5 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                  Sélection
                </th>
                <th className="px-4 md:px-5 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                  Photos Podiums
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {displayedEvents.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 md:px-5 py-6 text-center text-sm text-slate-500 dark:text-slate-300"
                  >
                    Aucun résultat ne correspond à vos filtres pour cette
                    saison.
                  </td>
                </tr>
              )}

              {displayedEvents.map((event) => (
                <tr
                  key={event.id}
                  className="hover:bg-slate-50/90 dark:hover:bg-slate-800/80 transition-colors"
                >
                  <td className="px-4 md:px-5 py-3 align-top text-slate-800 dark:text-slate-100 text-xs md:text-sm whitespace-nowrap">
                    <div className="font-semibold">
                      {formatDate(event.start)}
                    </div>
                    {event.cate_event && (
                      <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-300">
                        {event.cate_event}
                      </div>
                    )}
                  </td>

                  <td className="px-4 md:px-5 py-3 align-top text-slate-800 dark:text-slate-100 text-xs md:text-sm">
                    <div className="font-semibold mb-1">{event.titre}</div>
                    {event.type_event && (
                      <div className="inline-flex px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold">
                        {event.type_event} – {event.level_event}
                      </div>
                    )}
                  </td>

                  {/* Rapport */}
                  <td className="px-4 md:px-5 py-3 align-middle text-center">
                    {event.rapport ? (
                      <a
                        href={event.rapport}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100 dark:bg-slate-800 dark:text-red-400 dark:hover:bg-slate-700 transition"
                        title="Ouvrir le rapport (PDF)"
                      >
                        <FaFilePdf />
                      </a>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>

                  {/* Tableaux */}
                  <td className="px-4 md:px-5 py-3 align-middle text-center">
                    {event.tableaux ? (
                      <a
                        href={event.tableaux}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100 dark:bg-slate-800 dark:text-red-400 dark:hover:bg-slate-700 transition"
                        title="Ouvrir les tableaux (PDF)"
                      >
                        <FaFilePdf />
                      </a>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>

                  {/* Sélection */}
                  <td className="px-4 md:px-5 py-3 align-middle text-center">
                    {event.selection ? (
                      <a
                        href={event.selection}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100 dark:bg-slate-800 dark:text-red-400 dark:hover:bg-slate-700 transition"
                        title="Ouvrir la sélection (PDF)"
                      >
                        <FaFilePdf />
                      </a>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>

                  {/* Photos */}
                  <td className="px-4 md:px-5 py-3 align-middle text-center">
                    {event.photos_podiums ? (
                      <a
                        href={event.photos_podiums}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 transition"
                        title="Voir les photos de podiums"
                      >
                        <FaCamera />
                      </a>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default ResultatsPage;
